import React from 'react';
import { Loader2 } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPercent } from '../../utils/formatters';
import { StockMarketEntry } from '../../hooks/useStockMarket';

/**
 * "Info" tab for a stock / ETF detail page. The crypto CoinInfo tab reads rich
 * CoinGecko metadata; the Yahoo proxy has no such metadata, so this shows the
 * data we do have — price, 24h change, and the 52-week range — via the same
 * useStockMarket quote the Market tab uses. Stock-only; crypto uses CoinInfo.
 */

interface Props {
  name: string;
  symbol: string;
  type: 'stock' | 'etf';
  entry?: StockMarketEntry;
  loading: boolean;
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-100">{value}</p>
    </div>
  );
}

export function StockInfo({ name, symbol, type, entry, loading }: Props) {
  const { fmt } = useCurrency();

  if (loading && !entry) {
    return (
      <div className="py-10 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!entry) {
    return (
      <p className="text-sm text-slate-500 py-4">
        Live data for {name} is unavailable right now.
      </p>
    );
  }

  const up   = entry.usd_24h_change >= 0;
  const low  = entry.week52LowUsd;
  const high = entry.week52HighUsd;
  const pos =
    low != null && high != null && high > low
      ? Math.min(100, Math.max(0, ((entry.usd - low) / (high - low)) * 100))
      : null;

  return (
    <div className="max-w-2xl">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Price" value={entry.usd > 0 ? fmt(entry.usd) : '—'} />
        <Stat
          label="24h"
          value={
            <span className={up ? 'text-emerald-400' : 'text-red-400'}>
              {formatPercent(entry.usd_24h_change)}
            </span>
          }
        />
        <Stat label="52-Wk Low"  value={low  != null ? fmt(low)  : '—'} />
        <Stat label="52-Wk High" value={high != null ? fmt(high) : '—'} />
      </div>

      {pos != null && (
        <div className="mt-6 max-w-md">
          <div className="flex justify-between text-[11px] text-slate-500 mb-1.5">
            <span>52-week range</span>
            <span>{Math.round(pos)}%</span>
          </div>
          <div className="relative h-1.5 rounded-full bg-slate-800">
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-slate-100"
              style={{ left: `calc(${pos}% - 5px)` }}
            />
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-slate-600">
        {name} ({symbol}) · {type.toUpperCase()} · Prices from Yahoo Finance.
      </p>
    </div>
  );
}
